import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { useBranding } from '../branding/BrandingProvider.js';
import { Button } from '../components/Button.js';
import { PageHeader } from '../components/PageHeader.js';
import { ApiClientError, apiRequest } from '../lib/apiClient.js';

import { PasswordFields } from './PasswordFields.js';

/**
 * Plan E6. Shared by the reset and invite-acceptance flows, which differ only
 * in the endpoint and the heading.
 *
 * **One message for expired, consumed and unknown tokens** (§04.4). The three
 * are the same to the person holding the link, and distinguishing them would
 * tell someone with a stolen link whether it was ever real.
 *
 * The dead-link state is styled as a `warning`, never as `danger`: nothing has
 * gone wrong with anyone's account, the link simply aged out, and the platform
 * red is reserved for actions that change a SIM irreversibly (§11.6, and the
 * confinement scan in `sourceScan.spec.ts` that enforces it).
 */
const BAD_LINK =
  'This link has expired or has already been used. Ask your administrator for a new one.';

/**
 * Whose panel this is. A person reaches this screen from an email, with no
 * shell around it, so the mark is the only thing establishing that. Logo when
 * the tenant has one, product name when they do not.
 */
function BrandLockup(): ReactElement {
  const { branding } = useBranding();
  return branding?.logoUrl ? (
    <img src={branding.logoUrl} alt={branding.productName} className="h-9 w-auto self-start" />
  ) : (
    <p className="text-sm font-semibold text-body">{branding?.productName ?? 'Connect Panel'}</p>
  );
}

/** The one shape every state of this page takes: one card, centred, on the canvas. */
function AuthCard({ children }: { children: ReactNode }): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-alt px-[var(--space-6)] py-[var(--space-10)]">
      <div className="flex w-full max-w-md flex-col gap-[var(--space-6)] card p-[var(--space-8)] shadow-[var(--elevation-2)]">
        {children}
      </div>
    </main>
  );
}

export function ResetPasswordPage({ mode = 'reset' }: { mode?: 'reset' | 'invite' }): ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<ApiClientError | null>(null);
  const [badLink, setBadLink] = useState(false);
  const [busy, setBusy] = useState(false);

  const isInvite = mode === 'invite';
  const path = isInvite ? '/auth/invite/accept' : '/auth/password/reset';

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // The two endpoints spell the field differently, and both schemas are
      // `.strict()`: `/auth/invite/accept` takes `{ token, password }` while
      // `/auth/password/reset` takes `{ token, newPassword }`. Sending the
      // reset shape to the invite route was a guaranteed 422 with no field to
      // pin it on — the person accepting an invite saw a dead form.
      const body = isInvite ? { token, password: next } : { token, newPassword: next };
      await apiRequest(path, { method: 'POST', body });
      void navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (
          err.code === 'NOT_FOUND' ||
          (err.code === 'VALIDATION_FAILED' && err.details.length === 0)
        ) {
          setBadLink(true);
        } else {
          setError(err);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (badLink || token === '') {
    return (
      <AuthCard>
        <div className="flex flex-col gap-[var(--space-4)]">
          <BrandLockup />
          <span
            aria-hidden="true"
            className="flex size-10 items-center justify-center rounded-[var(--radius-md)] bg-warning-soft text-warning-text"
          >
            {/* Shape as well as hue, so the state does not depend on colour
                alone (WCAG 1.4.1) — the same icon+word pairing the pills use. */}
            <TriangleAlert size={18} strokeWidth={2} />
          </span>
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
            This link no longer works
          </h1>
          <p role="alert" className="text-sm text-body">
            {BAD_LINK}
          </p>
        </div>
        <Link
          to="/login"
          className="inline-flex items-center gap-[var(--space-2)] self-start text-sm text-primary underline underline-offset-2"
        >
          <ArrowLeft aria-hidden="true" size={14} strokeWidth={2} />
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      {/*
        `PageHeader` rather than a local `<h1>`: it is the one place the product
        decides what a screen title looks like, and an auth screen that drew its
        own would drift from it on the next edit. It carries its own bottom
        rule, so the card needs no separator of its own here.
      */}
      <div className="flex flex-col gap-[var(--space-4)]">
        <BrandLockup />
        <PageHeader
          title={isInvite ? 'Set up your account' : 'Choose a new password'}
          description={
            isInvite ? 'Choose a password to finish setting up your account.' : undefined
          }
        />
      </div>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex flex-col gap-[var(--space-4)]"
        noValidate
      >
        <PasswordFields
          error={error}
          value={next}
          confirmValue={confirm}
          onChange={setNext}
          onConfirmChange={setConfirm}
          label={isInvite ? 'Choose a password' : 'New password'}
        />
        <Button
          type="submit"
          loading={busy}
          disabled={next !== confirm || next.length === 0}
          className="mt-[var(--space-2)] h-11 w-full font-semibold shadow-[var(--elevation-2)]"
        >
          {isInvite ? 'Finish setting up' : 'Change password'}
        </Button>
      </form>
    </AuthCard>
  );
}

export function AcceptInvitePage(): ReactElement {
  return <ResetPasswordPage mode="invite" />;
}
